const TAB_ID_KEY = 'mcUploadTabId'
const LOCK_PREFIX = 'mc-file-upload:'
const LEASE_PREFIX = 'mcFileUploadLease:'
const HEARTBEAT_INTERVAL = 2000
export const UPLOAD_OWNER_TIMEOUT = 10000

const createId = () =>
  `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

const getSessionStorage = () => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

const sessionStorage = getSessionStorage()
let tabId = sessionStorage?.getItem(TAB_ID_KEY)
if (!tabId) {
  tabId = createId()
  sessionStorage?.setItem(TAB_ID_KEY, tabId)
}

const leaseKey = (uploadId) => `${LEASE_PREFIX}${uploadId}`

const readLease = (uploadId) => {
  try {
    return JSON.parse(localStorage.getItem(leaseKey(uploadId)))
  } catch {
    return null
  }
}

const writeLease = (uploadId, ownerTabId) => {
  const lease = {
    ownerTabId,
    expiresAt: Date.now() + UPLOAD_OWNER_TIMEOUT,
  }
  localStorage.setItem(leaseKey(uploadId), JSON.stringify(lease))
  return lease
}

const releaseLease = (uploadId) => {
  if (readLease(uploadId)?.ownerTabId === tabId) {
    localStorage.removeItem(leaseKey(uploadId))
  }
}

const runOwnedTask = async ({ uploadId, task, onOwnershipChange, useLease }) => {
  const publishHeartbeat = () => {
    if (useLease) writeLease(uploadId, tabId)
    onOwnershipChange?.({ ownerTabId: tabId, ownerHeartbeatAt: Date.now() })
  }

  publishHeartbeat()
  const heartbeat = window.setInterval(publishHeartbeat, HEARTBEAT_INTERVAL)

  try {
    return await task()
  } finally {
    window.clearInterval(heartbeat)
    if (useLease) releaseLease(uploadId)
    onOwnershipChange?.({ ownerTabId: null, ownerHeartbeatAt: null })
  }
}

const runWithLease = async (options) => {
  const currentLease = readLease(options.uploadId)
  if (
    currentLease?.ownerTabId !== tabId &&
    Number(currentLease?.expiresAt) > Date.now()
  ) {
    return { acquired: false }
  }

  writeLease(options.uploadId, tabId)
  await new Promise((resolve) => window.setTimeout(resolve, 50 + Math.random() * 50))
  if (readLease(options.uploadId)?.ownerTabId !== tabId) return { acquired: false }

  return {
    acquired: true,
    value: await runOwnedTask({ ...options, useLease: true }),
  }
}

export const getUploadTabId = () => tabId

export const isUploadOwnedByCurrentTab = (upload) => upload?.ownerTabId === tabId

export const isUploadOwnedByAnotherTab = (upload) =>
  Boolean(
    upload?.ownerTabId &&
      upload.ownerTabId !== tabId &&
      Date.now() - Number(upload.ownerHeartbeatAt || 0) < UPLOAD_OWNER_TIMEOUT
  )

export const withUploadOwnership = async (options) => {
  if (!navigator.locks?.request) return runWithLease(options)

  return navigator.locks.request(
    `${LOCK_PREFIX}${options.uploadId}`,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock) return { acquired: false }
      return {
        acquired: true,
        value: await runOwnedTask({ ...options, useLease: false }),
      }
    }
  )
}
